import { useEffect, useMemo, useState } from 'react';

const DEFAULT_MISSING_TRANSCRIPT_DELAY_MS = 5000;

// Persist the "no transcript yet" deadline across tab switches/remounts.
const missingTranscriptDeadlineByCallKey = new Map<string, number>();

interface UseMissingTranscriptDelayArgs {
  callerId: string;
  callStartTime: number | null;
  isFetching: boolean;
  tokenCount: number;
  delayMs?: number;
}

export default function useMissingTranscriptDelay({
  callerId,
  callStartTime,
  isFetching,
  tokenCount,
  delayMs = DEFAULT_MISSING_TRANSCRIPT_DELAY_MS,
}: UseMissingTranscriptDelayArgs): boolean {
  const [hasWaitedForTranscript, setHasWaitedForTranscript] = useState(false);

  const callKey = useMemo(
    () => `${callerId || ''}:${callStartTime || 0}`,
    [callerId, callStartTime],
  );

  useEffect(() => {
    // Reset and (maybe) start the delay timer for the "no transcript" message.
    setHasWaitedForTranscript(false);

    if (isFetching) return;
    if (tokenCount > 0) return;

    const now = Date.now();
    let deadline = missingTranscriptDeadlineByCallKey.get(callKey);
    if (typeof deadline !== 'number') {
      deadline = now + delayMs;
      missingTranscriptDeadlineByCallKey.set(callKey, deadline);
    }

    const remaining = deadline - now;
    if (remaining <= 0) {
      setHasWaitedForTranscript(true);
      return;
    }

    const t = window.setTimeout(() => {
      setHasWaitedForTranscript(true);
    }, remaining);

    return () => window.clearTimeout(t);
  }, [callKey, delayMs, isFetching, tokenCount]);

  return hasWaitedForTranscript;
}
